import { money, multiplyRational, type Money } from './money.js';

/**
 * Stock and purchasing rules (spec §13).
 *
 * On-hand is ALWAYS the sum of movements. There is no quantity column to
 * correct, which is what makes these guarantees hold:
 *
 *   - paying a supplier never creates stock (payments write no movements)
 *   - a goods receipt cannot be applied twice (its source key is unique)
 *   - taking one box deducts its configured pack size once — never an assumed
 *     whole box per treatment
 */

export interface Movement {
  kind:
    | 'receipt'
    | 'issue'
    | 'return'
    | 'transfer_in'
    | 'transfer_out'
    | 'waste'
    | 'expiry'
    | 'adjustment';
  /** Signed, in BASE units. Receipts positive, issues negative. */
  quantity: number;
}

export function onHand(movements: readonly Movement[]): number {
  return movements.reduce((total, m) => total + m.quantity, 0);
}

/** Packs as purchased converted to the base units stock is counted in. */
export function packsToBaseUnits(packQuantity: number, packSize: number): number {
  if (!Number.isInteger(packQuantity) || packQuantity <= 0) {
    throw new RangeError('Pack quantity must be a positive integer.');
  }
  if (!Number.isInteger(packSize) || packSize <= 0) {
    throw new RangeError('Pack size must be a positive integer.');
  }
  return packQuantity * packSize;
}

export type StockLevel = 'ok' | 'low' | 'out' | 'negative';

/**
 * Negative stock means the records disagree with reality — usually concurrent
 * offline consumption. It is surfaced as its own state rather than clamped to
 * zero, because hiding it loses the reconciliation signal (spec §13, §15).
 */
export function stockLevel(quantity: number, reorderThreshold: number): StockLevel {
  if (quantity < 0) return 'negative';
  // No threshold means the clinic has not asked to be warned about this item:
  // a catalogue entry that has never been stocked is not "out", it is unused.
  if (reorderThreshold <= 0) return 'ok';
  if (quantity === 0) return 'out';
  if (quantity <= reorderThreshold) return 'low';
  return 'ok';
}

export interface ExpiringBatch {
  lotNumber: string;
  expiresOn: string | null;
  quantity: number;
}

export function expiryState(
  batch: ExpiringBatch,
  asOf: Date,
  warnDays = 60,
): 'expired' | 'expiring_soon' | 'ok' | 'unknown' {
  if (!batch.expiresOn) return 'unknown';
  const expires = new Date(`${batch.expiresOn}T00:00:00Z`);
  if (Number.isNaN(expires.getTime())) return 'unknown';
  const days = Math.floor((expires.getTime() - asOf.getTime()) / 86_400_000);
  if (days < 0) return 'expired';
  if (days <= warnDays) return 'expiring_soon';
  return 'ok';
}

/**
 * Weighted-average cost, the documented costing method (spec §13).
 *
 * Chosen over FIFO because it stays correct when receipts arrive out of order
 * from offline devices, which FIFO does not without a reliable global ordering.
 * The method is recorded here so a valuation can be explained rather than
 * merely produced.
 */
export const COSTING_METHOD = 'weighted_average' as const;

export interface CostedReceipt {
  quantity: number;
  unitCostMinor: number;
  currency: string;
}

export function weightedAverageUnitCost(receipts: readonly CostedReceipt[], currency: string): Money {
  const usable = receipts.filter((r) => r.quantity > 0 && r.currency === currency);
  const totalQty = usable.reduce((q, r) => q + r.quantity, 0);
  if (totalQty === 0) return money(0, currency);
  const totalCost = usable.reduce((c, r) => c + r.quantity * r.unitCostMinor, 0);
  return money(Math.round(totalCost / totalQty), currency);
}

export function inventoryValue(quantityOnHand: number, unitCost: Money): Money {
  // Negative stock is valued at zero rather than as a negative asset; the
  // discrepancy is reported separately instead of distorting the valuation.
  if (quantityOnHand <= 0) return money(0, unitCost.currency);
  return multiplyRational(unitCost, quantityOnHand, 1);
}

/**
 * The purchasing flow's two distinct events (spec §13).
 *
 * Posting a BILL establishes the payable and price history.
 * Confirming RECEIPT creates stock movements.
 *
 * They are separate so entering an invoice for goods that have not arrived
 * cannot make stock appear. `postAndReceive` does both atomically for the
 * common case where the goods are already on the shelf.
 */
export type PurchaseAction = 'post_bill' | 'confirm_receipt' | 'post_and_receive';

export interface PurchaseEffects {
  createsPayable: boolean;
  recordsPriceHistory: boolean;
  createsStockMovements: boolean;
}

export function purchaseEffects(action: PurchaseAction): PurchaseEffects {
  switch (action) {
    case 'post_bill':
      return { createsPayable: true, recordsPriceHistory: true, createsStockMovements: false };
    case 'confirm_receipt':
      return { createsPayable: false, recordsPriceHistory: false, createsStockMovements: true };
    case 'post_and_receive':
      return { createsPayable: true, recordsPriceHistory: true, createsStockMovements: true };
  }
}

/** Deterministic key so a retry, an edit or a sync cannot receive goods twice. */
export function goodsReceiptKey(clinicId: string, purchaseInvoiceId: string, attempt = 1): string {
  return `${clinicId}:purchase_invoice:${purchaseInvoiceId}:receipt:${attempt}`;
}

export function purchasePayableKey(clinicId: string, purchaseInvoiceId: string): string {
  return `${clinicId}:purchase_invoice:${purchaseInvoiceId}:payable`;
}
